/**
 * "What lifts the band" — the exact pillar-lift projections for the surface
 * that records a site decision (the Decision tab's Decide section; the Path to
 * GO Decide phase that also read them was deleted in stage 3).
 *
 * Moved VERBATIM out of `pathToGo/DecisionStage.tsx` (Path to GO removal,
 * stage 1) so both surfaces read one implementation. No logic changed: every
 * band consequence is still the EXACT `projectPillarShift` re-run through the
 * real oracle, never a linear estimate (ENGINEERING RULE 6).
 */
import {
  perPillarMarginalDrag,
  projectPillarShift,
} from '../../../lib/pathToGo/distanceToGo';
import {
  DEFAULT_BANDS,
  DECISION_BAND_LABEL,
  type DecisionBand,
  type DecisionBandConfig,
  type DecisionBandId,
} from '../../../lib/portfolio/decisionBands';
import type { DerivedSiteVerdict } from '../../../lib/portfolio/deriveSiteVerdict';
import { PILLARS } from '../../../lib/portfolio/pillarRegistry';
import type { SiteComposite } from '../../../types/portfolioComposite';

/**
 * Target benefit score a "fix this pillar" projection assumes — a STRONG (not
 * perfect) pillar. 4.5/5 models a well-evidenced, low-risk pillar without
 * claiming a flawless 5.0; the projection itself is exact, this is only the
 * hypothetical the user is shown ("if you got this pillar to strong…").
 */
export const STRONG_PILLAR_TARGET = 4.5;

/** DEFAULT_BANDS ordered best→worst, for the "next band up" lookup. */
export const BANDS_BEST_FIRST: readonly DecisionBand[] = [...DEFAULT_BANDS].sort(
  (a, b) => a.min - b.min,
);

/** The band one rung BETTER than `bandId` (the next target), or null at GO. */
export function nextBetterBand(bandId: DecisionBandId): DecisionBand | null {
  const idx = BANDS_BEST_FIRST.findIndex((b) => b.id === bandId);
  if (idx <= 0) return null; // already best (GO) or unknown
  return BANDS_BEST_FIRST[idx - 1] ?? null;
}

/**
 * The current decision band id (camelCase) from the strict live verdict.
 * REG-SCORE #3 (scoring-engine audit, 2026-07-02) — adequacy-gated: a withheld
 * verdict can never surface the RAW composite.decisionBand (worst-case-shaped)
 * as "Current band: NO-GO" next to a pill that says "Insufficient data".
 */
export function decisionBandIdForVerdict(
  verdict: Pick<DerivedSiteVerdict, 'dataAdequate' | 'band'>,
): DecisionBandId | null {
  return verdict.dataAdequate && verdict.band != null
    ? verdict.band === 'conditional-high'
      ? 'conditionalHigh'
      : verdict.band === 'no-go'
        ? 'noGo'
        : verdict.band
    : null;
}

/** A single exact pillar-lift projection that changes the band. */
export interface LiftRow {
  pillarId: string;
  pillarLabel: string;
  toBand: DecisionBandId;
  toBandLabel: string;
}

/**
 * The exact what-lifts-the-band rows. For each pillar (ranked worst-drag first)
 * we run `projectPillarShift` to STRONG and keep ONLY the pillars whose exact
 * re-banded outcome is BETTER than the current band. Deterministic.
 */
export function computeLiftRows(
  composite: SiteComposite,
  currentBandId: DecisionBandId,
  bandConfig: DecisionBandConfig | null | undefined,
): LiftRow[] {
  const drag = perPillarMarginalDrag(composite);
  const currentRank = BANDS_BEST_FIRST.findIndex((b) => b.id === currentBandId);
  const rows: LiftRow[] = [];
  for (const d of drag) {
    // No point projecting a pillar already at/above the strong target.
    if (d.score >= STRONG_PILLAR_TARGET) continue;
    const proj = projectPillarShift(composite, d.pillarId, STRONG_PILLAR_TARGET, bandConfig);
    const projRank = BANDS_BEST_FIRST.findIndex((b) => b.id === proj.band.id);
    // Keep only genuine improvements (a strictly better band).
    if (proj.bandChanged && projRank >= 0 && projRank < currentRank) {
      rows.push({
        pillarId: d.pillarId,
        pillarLabel: PILLARS[d.pillarId].label,
        toBand: proj.band.id,
        toBandLabel: DECISION_BAND_LABEL[proj.band.id],
      });
    }
  }
  return rows;
}
